import { ESTIMATION_CHOICES, type EstimationDraft } from '../lib/estimation'

/**
 * Choosing how a variable amount is worked out.
 *
 * The options are labelled by what they do for the user rather than by their statistical
 * name: "worst recent one — plans for the bad month" is a decision someone can make about
 * their own money, where "trailing max" is a thing they would have to look up.
 */
export function EstimationFields({
  draft,
  setDraft,
  currency,
  fallbackLabel,
}: {
  draft: EstimationDraft
  setDraft: (d: EstimationDraft) => void
  currency: string
  fallbackLabel: string
}) {
  const needsSample =
    draft.kind !== 'FIXED' && draft.kind !== 'SEASONAL'

  return (
    <div className="cf-grid cf-subgrid">
      <label className="cf-wide">
        <span>Amount varies?</span>
        <select
          value={draft.kind}
          onChange={(e) => setDraft({ ...draft, kind: e.target.value as EstimationDraft['kind'] })}
        >
          {ESTIMATION_CHOICES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </label>

      {needsSample && (
        <label>
          <span>How many recent</span>
          <input
            value={draft.sample}
            inputMode="numeric"
            onChange={(e) => setDraft({ ...draft, sample: e.target.value })}
          />
        </label>
      )}

      {draft.kind === 'PERCENTILE' && (
        <label>
          <span>Worse than this % of them</span>
          <input
            value={draft.percentile}
            inputMode="numeric"
            onChange={(e) => setDraft({ ...draft, percentile: e.target.value })}
          />
        </label>
      )}

      {draft.kind === 'SEASONAL' && (
        <label>
          <span>Years to look back</span>
          <input
            value={draft.years}
            inputMode="numeric"
            onChange={(e) => setDraft({ ...draft, years: e.target.value })}
          />
        </label>
      )}

      {draft.kind === 'TRAILING_MEAN_WITH_GROWTH' && (
        <label>
          <span>Rising % a year</span>
          <input
            value={draft.growthPercent}
            inputMode="decimal"
            onChange={(e) => setDraft({ ...draft, growthPercent: e.target.value })}
          />
        </label>
      )}

      {draft.kind !== 'FIXED' && (
        <p className="cf-hint">
          {fallbackLabel} is used until there is history to work from ({currency}).
        </p>
      )}
    </div>
  )
}
